/**
 * ConsentToggleRow — Single permission row.
 *
 * Label + sublabel on left, React Native Switch on right.
 * Supports disabled state for cascading toggles.
 * WCAG AA: 52px minimum touch target, accessibilityRole="switch".
 */

import React, { useCallback } from 'react';
import { View, Text, Switch, StyleSheet } from 'react-native';
import { useTranslation } from '@dramadetector/shared';

interface ConsentToggleRowProps {
  label: string;
  sublabel?: string;
  value: boolean;
  onValueChange: (v: boolean) => void;
  disabled?: boolean;
  /** Green styling for crisis/safety toggle */
  variant?: 'default' | 'safety';
  /** dash = future select sheet placeholder; switch = interactive toggle */
  inputType?: 'switch' | 'dash';
  testID?: string;
  showDivider?: boolean;
}

const TRACK_COLOR_DEFAULT = { false: '#E4E4E7', true: '#A78BFA' };
const TRACK_COLOR_SAFETY = { false: '#E4E4E7', true: '#10B981' };
const THUMB_COLOR = '#FFFFFF';

export const ConsentToggleRow: React.FC<ConsentToggleRowProps> = React.memo(
  function ConsentToggleRow({
    label,
    sublabel,
    value,
    onValueChange,
    disabled = false,
    variant = 'default',
    inputType = 'switch',
    testID,
    showDivider = true,
  }) {
    const { t } = useTranslation('common');

    const trackColor =
      variant === 'safety' ? TRACK_COLOR_SAFETY : TRACK_COLOR_DEFAULT;

    const handleChange = useCallback(
      (v: boolean) => {
        if (!disabled) onValueChange(v);
      },
      [disabled, onValueChange]
    );

    return (
      <View
        style={[
          styles.row,
          showDivider && styles.rowBorder,
        ]}
        accessibilityRole="switch"
        accessibilityLabel={label}
        accessibilityValue={{ text: value ? t('on', { defaultValue: 'On' }) : t('off', { defaultValue: 'Off' }) }}
        accessibilityState={{ disabled: !!disabled }}
      >
        <View style={styles.labelContainer}>
          <Text style={[styles.label, disabled && styles.labelDisabled]}>
            {label}
          </Text>
          {sublabel ? (
            <Text style={styles.sublabel}>{sublabel}</Text>
          ) : null}
        </View>

        {inputType === 'switch' ? (
          <View style={disabled ? styles.switchDisabled : undefined}>
            <Switch
              value={value}
              onValueChange={handleChange}
              disabled={disabled}
              trackColor={trackColor}
              thumbColor={THUMB_COLOR}
              ios_backgroundColor="#E4E4E7"
              testID={testID}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityRole="switch"
              accessibilityLabel={label}
              accessibilityValue={{ text: value ? 'On' : 'Off' }}
              accessibilityState={{ disabled: !!disabled }}
            />
          </View>
        ) : (
          <Text style={styles.dash}>—</Text>
        )}
      </View>
    );
  }
);

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    minHeight: 52,
    gap: 12,
  },
  rowBorder: {
    borderBottomWidth: 0.5,
    borderBottomColor: 'rgba(255, 255, 255, 0.4)',
  },
  labelContainer: {
    flex: 1,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#334155',
  },
  labelDisabled: {
    color: '#A1A1AA',
  },
  sublabel: {
    fontSize: 11,
    color: '#A1A1AA',
    marginTop: 2,
  },
  switchDisabled: {
    opacity: 0.4,
  },
  dash: {
    fontSize: 12,
    color: '#A1A1AA',
    fontWeight: '500',
  },
});
